import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of, from } from 'rxjs';
import { catchError, mergeMap, map } from 'rxjs/operators';
import { SchemaApiErrorTypes, SchemaApiError } from './schema.errors';
import { SchemaProject } from 'src/app/admin/models/schema-project.model';
import { Schema } from 'src/app/admin/models/schema.model';
import { SchemaDefinition } from 'src/app/admin/models/schema-definition.model';
import cloneDeep from 'lodash-es/cloneDeep';

const PROJECTS_BASE_URL = '/admin/schema-projects';
const ADMIN_SCHEMAS_BASE_URL = '/admin/schemas';
const PUBLIC_SCHEMAS_BASE_URL = '/schemas';
const DEFINITIONS_BASE_URL = '/admin/schema-parts';
const MAX_SCHEMAS_PER_PAGE = 50;
const MAX_DEFINITIONS_PER_PAGE = 50;

@Injectable()
export class SchemaService {
  constructor(private http: HttpClient) {}

  loadProjects(): Observable<SchemaProject[]> {
    const url = `${environment.apiConfig.url}${PROJECTS_BASE_URL}`;

    return this.http
      .get<SchemaProject[]>(url)
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.LoadSchemaProjectsError, err)),
        ),
      );
  }

  createProject(name: string): Observable<SchemaProject> {
    const url = `${environment.apiConfig.url}${PROJECTS_BASE_URL}`;
    const newProject = { name };

    return this.http.post<SchemaProject>(url, newProject).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 409) {
          return throwError(
            new SchemaApiError(SchemaApiErrorTypes.CreateSchemaProjectConflictError, err),
          );
        }
        return throwError(new SchemaApiError(SchemaApiErrorTypes.CreateSchemaProjectError, err));
      }),
    );
  }

  deleteProject(projectId: number): Observable<any> {
    const url = `${environment.apiConfig.url}${PROJECTS_BASE_URL}/${projectId}`;

    return this.http
      .delete(url)
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.DeleteSchemaProjectError, err)),
        ),
      );
  }

  loadSchemas(projectId: number) {
    const url = `${environment.apiConfig.url}${ADMIN_SCHEMAS_BASE_URL}`;
    const params = new HttpParams({
      fromObject: {
        'filter[limit]': String(MAX_SCHEMAS_PER_PAGE),
        'filter[order]': 'datetimeAdded DESC',
        'filter[where][schemaProjectId]': String(projectId),
      },
    });

    return this.http
      .get<Schema[]>(url, { params })
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.LoadSchemasError, err)),
        ),
      );
  }

  loadSchema(schemaId: number) {
    const url = `${environment.apiConfig.url}${ADMIN_SCHEMAS_BASE_URL}/${schemaId}`;

    return this.http
      .get<Schema>(url)
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.SchemaNotFoundError, err)),
        ),
      );
  }

  loadActiveSchema() {
    const url = `${environment.apiConfig.url}${PUBLIC_SCHEMAS_BASE_URL}/active`;

    return this.http
      .get<Schema>(url)
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.SchemaNotFoundError, err)),
        ),
      );
  }

  getSchemaProject(projectId: number) {
    const url = `${environment.apiConfig.url}${PROJECTS_BASE_URL}/${projectId}`;

    return this.http
      .get<SchemaProject>(url)
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.SchemaProjectNotFoundError, err)),
        ),
      );
  }

  createSchema(
    schemaProjectId: number,
    description: string,
    values: Record<string, unknown>,
  ): Observable<Schema> {
    const url = `${environment.apiConfig.url}${ADMIN_SCHEMAS_BASE_URL}`;
    const newSchema = {
      schemaProjectId,
      description,
      values,
    };

    return this.http
      .post<Schema>(url, newSchema)
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.CreateSchemaError, err)),
        ),
      );
  }

  getLatestSchema(): Observable<Schema> {
    return this.loadProjects().pipe(
      mergeMap(([project]) => this.loadSchemas(project.id)),
      map((schemas) => schemas[0]),
    );
  }

  loadSchemaDefinitions(projectId: number): Observable<SchemaDefinition[]> {
    const url = `${environment.apiConfig.url}${DEFINITIONS_BASE_URL}`;
    const params = {
      filter: JSON.stringify({
        where: {
          schemaProjectId: projectId,
          datetimeDeleted: {
            eq: null,
          },
        },
      }),
    };

    return this.http
      .get<SchemaDefinition[]>(url, { params })
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.LoadSchemaDefinitionsError, err)),
        ),
      );
  }

  loadSchemaDefinitionVersions(projectId: number, name: string): Observable<SchemaDefinition[]> {
    const url = `${environment.apiConfig.url}${DEFINITIONS_BASE_URL}`;
    const params = {
      filter: JSON.stringify({
        where: {
          name,
          schemaProjectId: projectId,
        },
        order: 'datetimeAdded DESC',
        limit: MAX_DEFINITIONS_PER_PAGE,
      }),
    };

    return this.http.get<SchemaDefinition[]>(url, { params }).pipe(
      mergeMap((result: SchemaDefinition[]) => {
        if (result.length === 0) {
          return throwError(
            new SchemaApiError(
              SchemaApiErrorTypes.LoadSchemaDefinitionVersionsError,
              new Error('Not found'),
            ),
          );
        }
        return of(result);
      }),
      catchError((err) =>
        throwError(new SchemaApiError(SchemaApiErrorTypes.LoadSchemaDefinitionVersionsError, err)),
      ),
    );
  }

  loadSchemaDefinition(id: number): Observable<SchemaDefinition> {
    const url = `${environment.apiConfig.url}${DEFINITIONS_BASE_URL}/${id}`;

    return this.http
      .get<SchemaDefinition>(url)
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.LoadSchemaDefinitionError, err)),
        ),
      );
  }

  createSchemaDefinition(
    schemaProjectId: number,
    name: string,
    title: string,
    description: string,
    values: Record<string, unknown>,
  ): Observable<SchemaDefinition> {
    const url = `${environment.apiConfig.url}${DEFINITIONS_BASE_URL}`;
    const newSchema = {
      schemaProjectId,
      name,
      title,
      description,
      values,
    };

    return this.http
      .post<SchemaDefinition>(url, newSchema)
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.CreateSchemaDefinitionError, err)),
        ),
      );
  }

  deleteSchemaDefinition(id: number): Observable<any> {
    const url = `${environment.apiConfig.url}${DEFINITIONS_BASE_URL}/${id}`;

    return this.http
      .delete(url)
      .pipe(
        catchError((err) =>
          throwError(new SchemaApiError(SchemaApiErrorTypes.DeleteSchemaDefinitionError, err)),
        ),
      );
  }

  dereferenceSchema(rawSchema: any): Observable<any> {
    // @ts-ignore
    if ($RefParser) {
      // @ts-ignore
      return from($RefParser.dereference(cloneDeep(rawSchema.values))); // clone to prevent mutation of rawSchema
    }
    return throwError(new Error('Unable to load Ref Parser for dereferencing'));
  }
}
